import { useEffect, useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet, View } from "react-native";

import {
  AppText,
  Button,
  ButtonText,
  Card,
  DetailRow,
  EmptyState,
  ProtectedScreen,
  SearchableSelect,
  SectionHeader,
  Stack,
  TextField,
} from "~/components/ui";
import {
  usePrimaryWorkspace,
  useWorkspaceClients,
  useWorkspaceInvoices,
  useWorkspaceMembership,
  useWorkspaceProjects,
  useWorkspaceRecurringInvoices,
  useWorkspaceTasks,
  useWorkspaceTimeEntries,
} from "~/hooks/workspace";
import { useAuth } from "~/hooks/useAuth";
import { useWorkspaceSubscription } from "~/hooks/useSubscription";
import {
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import { invoiceRoundingLabel } from "~/utils/invoice-rounding";

function normalizeParam(value: string | string[] | undefined) {
  return Array.isArray(value) ? value[0] : value;
}

function formatMoney(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function todayPlus(days: number) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function formatHours(seconds: number) {
  const hours = seconds / 3600;
  return `${hours.toFixed(hours >= 10 ? 0 : 1)}h`;
}

function billableValue(entry: {
  duration_seconds: number;
  hourly_rate_cents: number | null;
}) {
  if (!entry.hourly_rate_cents) return 0;
  return Math.round((entry.duration_seconds / 3600) * entry.hourly_rate_cents);
}

const CADENCE_OPTIONS = [
  { label: "Weekly", value: "weekly" },
  { label: "Fortnightly", value: "fortnightly" },
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
];

export default function ClientDetailScreen() {
  const stylesheet = useThemedStyles(makeStyles);
  const router = useRouter();
  const { user } = useAuth();
  const params = useLocalSearchParams<{ id?: string | string[] }>();
  const clientId = normalizeParam(params.id);
  const [projectName, setProjectName] = useState("");
  const [projectBudget, setProjectBudget] = useState("");
  const [projectHourlyRate, setProjectHourlyRate] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [invoiceDueDate, setInvoiceDueDate] = useState(todayPlus(14));
  const [invoiceNotes, setInvoiceNotes] = useState("");
  const [automationTitle, setAutomationTitle] = useState("");
  const [automationCadence, setAutomationCadence] = useState("monthly");
  const [automationNextIssueDate, setAutomationNextIssueDate] = useState(
    todayPlus(30),
  );
  const [automationDueDays, setAutomationDueDays] = useState("14");
  const [automationDescription, setAutomationDescription] = useState("");
  const [automationAmount, setAutomationAmount] = useState("");
  const [automationGstRate, setAutomationGstRate] = useState("");
  const [automationPaymentInstructions, setAutomationPaymentInstructions] =
    useState("");
  const [automationEndsOn, setAutomationEndsOn] = useState("");
  const [automationLimit, setAutomationLimit] = useState("");
  const workspace = usePrimaryWorkspace();
  const workspaceMembership = useWorkspaceMembership(workspace.data?.id);
  const workspaceSubscription = useWorkspaceSubscription(workspace.data?.id);
  const clients = useWorkspaceClients(workspace.data?.id);
  const projects = useWorkspaceProjects(workspace.data?.id);
  const tasks = useWorkspaceTasks(workspace.data?.id);
  const timeEntries = useWorkspaceTimeEntries(workspace.data?.id);
  const invoices = useWorkspaceInvoices(workspace.data?.id);
  const recurringInvoices = useWorkspaceRecurringInvoices(workspace.data?.id);

  const client =
    (clients.data ?? []).find((record) => record.id === clientId) ?? null;
  const clientProjects = (projects.data ?? []).filter(
    (project) => project.client_id === client?.id,
  );
  const projectIds = new Set(clientProjects.map((project) => project.id));
  const clientTasks = (tasks.data ?? []).filter(
    (task) => task.project_id && projectIds.has(task.project_id),
  );
  const clientTimeEntries = (timeEntries.data ?? []).filter(
    (entry) =>
      entry.client_id === client?.id ||
      (entry.project_id ? projectIds.has(entry.project_id) : false),
  );
  const clientInvoices = (invoices.data ?? []).filter(
    (invoice) => invoice.client_id === client?.id,
  );
  const clientRecurringInvoices = (recurringInvoices.data ?? []).filter(
    (template) => template.client_id === client?.id,
  );
  const canManageWorkspace = Boolean(
    workspace.data?.owner_id === user?.id ||
    workspaceMembership.data?.role === "admin" ||
    workspaceMembership.data?.role === "owner",
  );
  const canManageAutomation =
    canManageWorkspace && workspaceSubscription.canUseRecurringInvoices;
  const openProjects = clientProjects.filter((project) => !project.archived);
  const openTasks = clientTasks.filter(
    (task) => task.status !== "done" && task.status !== "archived",
  );
  const trackedSeconds = clientTimeEntries.reduce(
    (total, entry) => total + entry.duration_seconds,
    0,
  );
  const unbilledCents = clientTimeEntries
    .filter((entry) => entry.billable && !entry.invoiced_at)
    .reduce((total, entry) => total + billableValue(entry), 0);
  const outstandingCents = clientInvoices
    .filter((invoice) => !invoice.paid_at && invoice.status !== "void")
    .reduce((total, invoice) => total + invoice.total_cents, 0);
  const paidCents = clientInvoices
    .filter((invoice) => invoice.paid_at)
    .reduce((total, invoice) => total + invoice.total_cents, 0);
  const error =
    workspace.error ??
    clients.error ??
    clients.setArchived.error ??
    projects.error ??
    projects.create.error ??
    tasks.error ??
    timeEntries.error ??
    invoices.error ??
    invoices.create.error ??
    recurringInvoices.error ??
    recurringInvoices.create.error;
  const errorMessage = error instanceof Error ? error.message : "";

  useEffect(() => {
    if (!workspace.data || automationGstRate) return;
    setAutomationGstRate(workspace.data.gst_registered ? "15" : "0");
  }, [automationGstRate, workspace.data]);

  async function createClientProject() {
    if (!client || !projectName.trim()) return;
    await projects.create.mutateAsync({
      budget: projectBudget,
      clientId: client.id,
      description: projectDescription,
      endsOn: "",
      hourlyRate: projectHourlyRate,
      name: projectName,
      startsOn: "",
      statusId: "",
    });
    setProjectName("");
    setProjectBudget("");
    setProjectHourlyRate("");
    setProjectDescription("");
  }

  async function createClientInvoice() {
    if (!client || !invoiceDueDate.trim()) return;
    await invoices.create.mutateAsync({
      clientId: client.id,
      dueDate: invoiceDueDate,
      notes: invoiceNotes,
    });
    setInvoiceDueDate(todayPlus(14));
    setInvoiceNotes("");
  }

  async function createClientAutomation() {
    if (
      !client ||
      !canManageAutomation ||
      !automationTitle.trim() ||
      !automationDescription.trim() ||
      !(Number(automationAmount) > 0)
    ) {
      return;
    }
    await recurringInvoices.create.mutateAsync({
      cadence: automationCadence,
      clientId: client.id,
      dueDays: automationDueDays,
      endsOn: automationEndsOn,
      lineItems: JSON.stringify([
        {
          description: automationDescription,
          gstRate: automationGstRate,
          quantity: "1",
          unitAmount: automationAmount,
        },
      ]),
      maxOccurrences: automationLimit,
      nextIssueDate: automationNextIssueDate,
      notes: "",
      paymentInstructions: automationPaymentInstructions,
      title: automationTitle,
    });
    setAutomationTitle("");
    setAutomationCadence("monthly");
    setAutomationNextIssueDate(todayPlus(30));
    setAutomationDueDays("14");
    setAutomationDescription("");
    setAutomationAmount("");
    setAutomationPaymentInstructions("");
    setAutomationEndsOn("");
    setAutomationLimit("");
  }

  if (workspace.isLoading || clients.isLoading) {
    return (
      <ProtectedScreen title="Client" subtitle="Loading client details.">
        <Card gap="sm">
          <AppText>Loading...</AppText>
        </Card>
      </ProtectedScreen>
    );
  }

  if (!client) {
    return (
      <ProtectedScreen title="Client not found">
        <Card gap="md">
          <AppText size="sm">
            This client could not be found in the current workspace.
          </AppText>
          <Button
            align="start"
            variant="outline"
            onPress={() => router.push("/clients" as never)}
          >
            <ButtonText>Back to clients</ButtonText>
          </Button>
        </Card>
      </ProtectedScreen>
    );
  }

  return (
    <ProtectedScreen
      title={client.name}
      subtitle="Review billing context, active work, tracked time, and invoices for this client."
    >
      {errorMessage ? (
        <Card danger gap="xs">
          <AppText tone="danger" weight="semibold">
            Client details could not load
          </AppText>
          <AppText tone="danger" size="xs">
            {errorMessage}
          </AppText>
        </Card>
      ) : null}

      <View style={stylesheet.summaryGrid}>
        <Card gap="xs">
          <AppText size="xs">Open projects</AppText>
          <AppText size="lg" weight="bold">
            {String(openProjects.length)}
          </AppText>
        </Card>
        <Card gap="xs">
          <AppText size="xs">Open tasks</AppText>
          <AppText size="lg" weight="bold">
            {String(openTasks.length)}
          </AppText>
        </Card>
        <Card gap="xs">
          <AppText size="xs">Tracked time</AppText>
          <AppText size="lg" weight="bold">
            {formatHours(trackedSeconds)}
          </AppText>
        </Card>
        <Card gap="xs">
          <AppText size="xs">Outstanding</AppText>
          <AppText size="lg" weight="bold">
            {formatMoney(outstandingCents)}
          </AppText>
        </Card>
      </View>

      <Card gap="md">
        <SectionHeader
          title="Client actions"
          description="Start client-linked work or billing without leaving this client record."
        />
        <View style={stylesheet.actions}>
          <Button
            align="start"
            compact
            iconName={client.archived ? "archive-restore" : "archive"}
            variant="outline"
            disabled={clients.setArchived.isPending}
            onPress={() =>
              void clients.setArchived.mutateAsync({
                id: client.id,
                archived: !client.archived,
              })
            }
          >
            <ButtonText>
              {client.archived ? "Restore client" : "Archive client"}
            </ButtonText>
          </Button>
        </View>
        <View style={stylesheet.actionGrid}>
          <View style={stylesheet.actionPanel}>
            <SectionHeader
              title="New project"
              description={`Create a project already linked to ${client.name}.`}
            />
            <View style={stylesheet.formGrid}>
              <View style={[stylesheet.field, stylesheet.fullWidth]}>
                <AppText size="xs" weight="semibold">
                  Project name
                </AppText>
                <TextField
                  value={projectName}
                  placeholder="Website refresh"
                  onChangeText={setProjectName}
                />
              </View>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  Budget
                </AppText>
                <TextField
                  value={projectBudget}
                  placeholder="2500"
                  keyboardType="numeric"
                  onChangeText={setProjectBudget}
                />
              </View>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  Hourly rate
                </AppText>
                <TextField
                  value={projectHourlyRate}
                  placeholder="120"
                  keyboardType="numeric"
                  onChangeText={setProjectHourlyRate}
                />
              </View>
              <View style={[stylesheet.field, stylesheet.fullWidth]}>
                <AppText size="xs" weight="semibold">
                  Description
                </AppText>
                <TextField
                  value={projectDescription}
                  placeholder="Scope notes or delivery context"
                  onChangeText={setProjectDescription}
                />
              </View>
            </View>
            <Button
              align="start"
              compact
              iconName="folder-plus"
              disabled={!projectName.trim() || projects.create.isPending}
              onPress={() => void createClientProject()}
            >
              <ButtonText>
                {projects.create.isPending ? "Creating..." : "Create project"}
              </ButtonText>
            </Button>
          </View>

          <View style={stylesheet.actionPanel}>
            <SectionHeader
              title="New draft invoice"
              description="Start an invoice addressed to this client."
            />
            <View style={stylesheet.formGrid}>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  Due date
                </AppText>
                <TextField
                  value={invoiceDueDate}
                  placeholder="2026-07-20"
                  onChangeText={setInvoiceDueDate}
                />
              </View>
              <View style={[stylesheet.field, stylesheet.fullWidth]}>
                <AppText size="xs" weight="semibold">
                  Notes
                </AppText>
                <TextField
                  value={invoiceNotes}
                  placeholder="Payment details or invoice notes"
                  onChangeText={setInvoiceNotes}
                />
              </View>
            </View>
            <Button
              align="start"
              compact
              iconName="file-plus"
              disabled={!invoiceDueDate.trim() || invoices.create.isPending}
              onPress={() => void createClientInvoice()}
            >
              <ButtonText>
                {invoices.create.isPending
                  ? "Creating..."
                  : "Create draft invoice"}
              </ButtonText>
            </Button>
          </View>
        </View>
      </Card>

      <Card gap="md">
        <SectionHeader
          title="Invoice automation"
          description={`Create a recurring invoice schedule for ${client.name}. Each issued invoice snapshots the client and workspace billing defaults in effect at that time.`}
        />

        {workspaceSubscription.isLoading || workspaceMembership.isLoading ? (
          <AppText size="sm">Loading invoice automation...</AppText>
        ) : !workspaceSubscription.canUseRecurringInvoices ? (
          <View style={stylesheet.actionPanel}>
            <AppText size="sm" weight="semibold">
              Recurring invoices need an active MintStint subscription.
            </AppText>
            <Button
              align="start"
              compact
              variant="outline"
              onPress={() => router.push("/subscription" as never)}
            >
              <ButtonText>View subscription</ButtonText>
            </Button>
          </View>
        ) : !canManageWorkspace ? (
          <AppText size="sm">
            A workspace owner or admin can change invoice automation.
          </AppText>
        ) : (
          <>
            <View style={stylesheet.formGrid}>
              <View style={[stylesheet.field, stylesheet.fullWidth]}>
                <AppText size="xs" weight="semibold">
                  Schedule name
                </AppText>
                <TextField
                  value={automationTitle}
                  placeholder="Monthly design retainer"
                  onChangeText={setAutomationTitle}
                />
              </View>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  Cadence
                </AppText>
                <SearchableSelect
                  title="Choose cadence"
                  value={automationCadence}
                  options={CADENCE_OPTIONS}
                  onValueChange={setAutomationCadence}
                />
              </View>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  First issue date
                </AppText>
                <TextField
                  value={automationNextIssueDate}
                  placeholder="2026-08-01"
                  onChangeText={setAutomationNextIssueDate}
                />
              </View>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  Payment due days
                </AppText>
                <TextField
                  value={automationDueDays}
                  placeholder="14"
                  keyboardType="numeric"
                  onChangeText={setAutomationDueDays}
                />
              </View>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  Invoice amount
                </AppText>
                <TextField
                  value={automationAmount}
                  placeholder="1200"
                  keyboardType="numeric"
                  onChangeText={setAutomationAmount}
                />
              </View>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  GST %
                </AppText>
                <TextField
                  value={automationGstRate}
                  placeholder="15"
                  keyboardType="numeric"
                  onChangeText={setAutomationGstRate}
                />
              </View>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  End date (optional)
                </AppText>
                <TextField
                  value={automationEndsOn}
                  placeholder="2027-06-30"
                  onChangeText={setAutomationEndsOn}
                />
              </View>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  Invoice limit (optional)
                </AppText>
                <TextField
                  value={automationLimit}
                  placeholder="12"
                  keyboardType="numeric"
                  onChangeText={setAutomationLimit}
                />
              </View>
              <View style={[stylesheet.field, stylesheet.fullWidth]}>
                <AppText size="xs" weight="semibold">
                  Invoice line description
                </AppText>
                <TextField
                  value={automationDescription}
                  placeholder="Monthly design and support retainer"
                  onChangeText={setAutomationDescription}
                />
              </View>
              <View style={[stylesheet.field, stylesheet.fullWidth]}>
                <AppText size="xs" weight="semibold">
                  Payment instructions
                </AppText>
                <TextField
                  value={automationPaymentInstructions}
                  placeholder="Override the workspace default for this recurring schedule"
                  onChangeText={setAutomationPaymentInstructions}
                />
              </View>
            </View>
            <Button
              align="start"
              compact
              iconName="repeat"
              disabled={
                !automationTitle.trim() ||
                !automationDescription.trim() ||
                !(Number(automationAmount) > 0) ||
                recurringInvoices.create.isPending
              }
              onPress={() => void createClientAutomation()}
            >
              <ButtonText>
                {recurringInvoices.create.isPending
                  ? "Creating..."
                  : "Create recurring schedule"}
              </ButtonText>
            </Button>
          </>
        )}

        {clientRecurringInvoices.length === 0 ? (
          <EmptyState title="No recurring schedules for this client." />
        ) : (
          <Stack gap="sm">
            {clientRecurringInvoices.map((template) => (
              <View key={template.id} style={stylesheet.itemRow}>
                <View style={stylesheet.itemMain}>
                  <AppText weight="semibold">{template.title}</AppText>
                  <AppText size="xs">
                    {template.cadence} - next {template.next_issue_date} -{" "}
                    {template.generated_count} generated
                  </AppText>
                  <AppText size="xs">
                    {template.enabled ? "Active" : "Paused or complete"}
                    {template.ends_on ? ` - ends ${template.ends_on}` : ""}
                  </AppText>
                </View>
                <Button
                  compact
                  variant="outline"
                  onPress={() => router.push("/invoices" as never)}
                >
                  <ButtonText>Manage</ButtonText>
                </Button>
              </View>
            ))}
          </Stack>
        )}
      </Card>

      <View style={stylesheet.detailGrid}>
        <Card gap="md">
          <SectionHeader
            title="Contact and billing"
            description="Details used for invoice sending and client follow-up."
          />
          <Stack gap="sm">
            <DetailRow label="Contact" value={client.contact_name ?? "None"} />
            <DetailRow label="Email" value={client.email ?? "None"} />
            <DetailRow label="Phone" value={client.phone ?? "None"} />
            <DetailRow label="GST number" value={client.gst_number ?? "None"} />
            <DetailRow
              label="Invoice rounding"
              value={invoiceRoundingLabel(client.invoice_rounding_override)}
            />
          </Stack>
          {client.billing_address ? (
            <View style={stylesheet.noteBlock}>
              <AppText size="xs" weight="semibold">
                Billing address
              </AppText>
              <AppText size="sm">{client.billing_address}</AppText>
            </View>
          ) : null}
          {client.notes ? (
            <View style={stylesheet.noteBlock}>
              <AppText size="xs" weight="semibold">
                Notes
              </AppText>
              <AppText size="sm">{client.notes}</AppText>
            </View>
          ) : null}
        </Card>

        <Card gap="md">
          <SectionHeader
            title="Billing position"
            description="Invoice and unbilled time totals for this client."
          />
          <Stack gap="sm">
            <DetailRow label="Paid invoices" value={formatMoney(paidCents)} />
            <DetailRow
              label="Outstanding invoices"
              value={formatMoney(outstandingCents)}
            />
            <DetailRow
              label="Unbilled billable time"
              value={formatMoney(unbilledCents)}
            />
            <DetailRow label="Invoices" value={String(clientInvoices.length)} />
          </Stack>
        </Card>
      </View>

      <View style={stylesheet.detailGrid}>
        <Card gap="md">
          <SectionHeader
            title="Projects"
            description="Active and archived work for this client."
          />
          {clientProjects.length === 0 ? (
            <EmptyState title="No projects are linked to this client." />
          ) : (
            <Stack gap="sm">
              {clientProjects.slice(0, 8).map((project) => (
                <View key={project.id} style={stylesheet.itemRow}>
                  <View style={stylesheet.itemMain}>
                    <AppText weight="semibold">{project.name}</AppText>
                    <AppText size="xs">
                      {project.archived ? "Archived" : "Open"} -{" "}
                      {project.starts_on ?? "No start"} to{" "}
                      {project.ends_on ?? "No end"}
                    </AppText>
                  </View>
                  <View style={stylesheet.itemActions}>
                    <AppText size="xs" weight="semibold">
                      {project.budget_cents
                        ? formatMoney(project.budget_cents)
                        : "No budget"}
                    </AppText>
                    <Button
                      compact
                      variant="outline"
                      onPress={() =>
                        router.push(`/projects/${project.id}` as never)
                      }
                    >
                      <ButtonText>View</ButtonText>
                    </Button>
                  </View>
                </View>
              ))}
            </Stack>
          )}
        </Card>

        <Card gap="md">
          <SectionHeader
            title="Open tasks"
            description="Project-linked tasks that still need attention."
          />
          {openTasks.length === 0 ? (
            <EmptyState title="No open tasks for this client." />
          ) : (
            <Stack gap="sm">
              {openTasks.slice(0, 8).map((task) => (
                <View key={task.id} style={stylesheet.itemRow}>
                  <View style={stylesheet.itemMain}>
                    <AppText weight="semibold">{task.name}</AppText>
                    <AppText size="xs">
                      {task.status} - {task.due_on ?? "No due date"}
                    </AppText>
                  </View>
                  <View style={stylesheet.itemActions}>
                    <AppText size="xs" weight="semibold">
                      {task.billable ? "Billable" : "Non-billable"}
                    </AppText>
                    <Button
                      compact
                      variant="outline"
                      onPress={() => router.push(`/tasks/${task.id}` as never)}
                    >
                      <ButtonText>View</ButtonText>
                    </Button>
                  </View>
                </View>
              ))}
            </Stack>
          )}
        </Card>
      </View>

      <Card gap="md">
        <SectionHeader
          title="Invoices"
          description="Recent invoices linked to this client."
        />
        {clientInvoices.length === 0 ? (
          <EmptyState title="No invoices have been created for this client." />
        ) : (
          <Stack gap="sm">
            {clientInvoices.slice(0, 10).map((invoice) => (
              <View key={invoice.id} style={stylesheet.itemRow}>
                <View style={stylesheet.itemMain}>
                  <AppText weight="semibold">{invoice.invoice_number}</AppText>
                  <AppText size="xs">
                    {invoice.status} - due {invoice.due_date ?? "not set"}
                  </AppText>
                </View>
                <View style={stylesheet.itemActions}>
                  <AppText size="xs" weight="semibold">
                    {formatMoney(invoice.total_cents)}
                  </AppText>
                  <Button
                    compact
                    variant="outline"
                    onPress={() =>
                      router.push(`/invoices/${invoice.id}` as never)
                    }
                  >
                    <ButtonText>View</ButtonText>
                  </Button>
                </View>
              </View>
            ))}
          </Stack>
        )}
      </Card>

      <View style={stylesheet.actions}>
        <Button
          align="start"
          variant="outline"
          onPress={() => router.push("/clients" as never)}
        >
          <ButtonText>Back to clients</ButtonText>
        </Button>
      </View>
    </ProtectedScreen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    summaryGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    detailGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    actionGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    actionPanel: {
      minWidth: 280,
      flex: 1,
      gap: spacing.m,
      padding: spacing.m,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      backgroundColor: colors.background,
    },
    formGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.s,
    },
    field: {
      minWidth: 160,
      flex: 1,
      gap: spacing.xs,
    },
    fullWidth: {
      flexBasis: "100%",
    },
    noteBlock: {
      gap: spacing.xs,
      padding: spacing.m,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      backgroundColor: colors.surface,
    },
    itemRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.m,
      padding: spacing.m,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      backgroundColor: colors.background,
    },
    itemMain: {
      flex: 1,
      minWidth: 220,
      gap: spacing.xs,
    },
    itemActions: {
      alignItems: "flex-end",
      gap: spacing.s,
    },
    actions: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.s,
    },
  });
